import * as v from 'valibot'
import { DecorationBadgeDataSchema } from 'src/features/UserDecoration/schema/decoration'
import type { Decoration } from 'src/services/apollo/generated/graphql'
import {
  DecorationType,
  type UserBaseFragment,
} from 'src/services/apollo/generated/graphql'
import { userHasMembership } from 'src/utils/user'

export type ResolvedBadge =
  | { type: 'membership' }
  | {
      type: 'decoration'
      decorationData: v.InferOutput<typeof DecorationBadgeDataSchema>
      userRemark?: string
    }

type ResolvedDecoration = {
  decoration: Decoration
  userRemark?: string
}

/**
 * Resolve badge with all computed properties
 */
export function resolveAvatarBadgeData(
  user: Pick<UserBaseFragment, 'membership' | 'activeDecorations'>,
  decorationBadge?: Decoration | boolean,
): ResolvedBadge | undefined {
  const resolvedBadge = resolveBadgeDecorationWithRemark(user, decorationBadge)

  if (!resolvedBadge) return undefined
  if (resolvedBadge === 'membership') return { type: 'membership' }

  const res = v.safeParse(
    DecorationBadgeDataSchema,
    resolvedBadge.decoration.data,
  )
  if (!res.success) return undefined

  return {
    type: 'decoration',
    decorationData: res.output,
    userRemark: resolvedBadge.userRemark,
  }
}

/**
 * Determine which badge to show (with remark support)
 */
function resolveBadgeDecorationWithRemark(
  user: Pick<UserBaseFragment, 'membership' | 'activeDecorations'>,
  decoration?: Decoration | boolean,
): ResolvedDecoration | 'membership' | undefined {
  if (!decoration) return undefined // Explicitly disabled
  if (decoration !== true) return { decoration } // Decoration from prop; Remark text not available

  const userActiveBadgeDecoration = user.activeDecorations?.find(
    it => it.decoration.type === DecorationType.Badge,
  )

  if (userActiveBadgeDecoration)
    // User's active decoration and remark text
    return {
      decoration: userActiveBadgeDecoration.decoration,
      userRemark: userActiveBadgeDecoration.userRemark,
    }
  if (userHasMembership(user)) return 'membership'
  return undefined
}
